import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { useGetAccountQuery } from '../../../../redux/api/fymAPI';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { ProfileAddressCard } from '../../../smartLinks/profile/ProfileAddressCard';
import { ProfileCustomDomainCard } from '../../../smartLinks/profile/ProfileCustomDomainCard';
import { ProfileSaveBar } from '../../../smartLinks/profile/ProfileSaveBar';
import { AddressState, useAccountSlugCheck } from '../../../smartLinks/profile/useAccountSlugCheck';
import { NoArtistCard } from '../artist/NoArtistCard';
import { artistSettingsMessages } from '../artist/artistSettingsMessages';
import { TrackersSection } from './TrackersSection';
import { useAddressForm } from './useAddressForm';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Settings → SmartLinks (design artist-settings b1, b2): the page address, the custom domain and
 * the trackers of the active artist. It replaces the address part of the old Edit Profile page
 * and the old Manage Trackers page.
 */
const SmartLinksTabComponent: React.FC = () => {
  const intl = useIntl();
  const accountId = useAccountId();
  const { data, error, isLoading, refetch } = useGetAccountQuery(
    { accountId: accountId ?? 0 },
    { skip: accountId === null }
  );
  const account = data?.account;
  const form = useAddressForm(account);
  const check = useAccountSlugCheck(form.values.slug, account?.id, form.initial.slug);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const { save } = form;
  const onSave = useCallback(() => {
    save().catch(console.error);
  }, [save]);

  if (accountId === null) {
    return (
      <div className={cards.pane}>
        <NoArtistCard />
      </div>
    );
  }
  if (error !== undefined) {
    return (
      <div className={cards.pane}>
        <ErrorView error={error} retry={retry} />
      </div>
    );
  }
  if (isLoading || account === undefined) {
    return (
      <div className={cards.pane}>
        <LoadingView />
      </div>
    );
  }

  const canSave =
    form.isDirty &&
    form.values.slug.trim() !== '' &&
    check.state !== AddressState.taken &&
    check.state !== AddressState.illegal &&
    check.state !== AddressState.checking;

  return (
    <div className={cards.pane}>
      {form.error !== undefined && <ErrorAlert error={form.error} />}
      <ProfileAddressCard
        accountId={account.id}
        slug={form.values.slug}
        platformDomain={form.values.platformDomain}
        savedSlug={form.initial.slug}
        savedPlatformDomain={form.initial.platformDomain}
        isAddressDirty={form.isDirty}
        check={check}
        onSlugChange={form.setSlug}
        onPlatformDomainChange={form.setPlatformDomain}
        disabled={form.isSaving}
      />
      <ProfileCustomDomainCard customDomain={account.customDomain} />
      <ProfileSaveBar
        dirtyCount={form.isDirty ? 1 : 0}
        isAddressDirty={form.isDirty}
        canSave={canSave}
        isSaving={form.isSaving}
        onDiscard={form.discard}
        onSave={onSave}
        saveLabel={intl.formatMessage(artistSettingsMessages.saveAddress)}
      />
      <TrackersSection accountId={account.id} />
    </div>
  );
};

export const SmartLinksTab = TypedMemo(SmartLinksTabComponent);
